/**
 * (后期可拆出一个独立的Bubble组件, 专门用于需要通过referenceElement修改气泡定位的需求)
 * 表头的每个单元格可能会有两个气泡提示.
 *  * 没有使用Bubble组件包裹元素实现气泡的方案.
 *  * 采用一个Bubble组件, 通过改变referenceElement, 修改气泡定位; 减少Bubble渲染
 *  * 通过使用setTimeout延迟气泡关闭, 达到可以让鼠标滑动到气泡内部, 滚动气泡内容的目的
 *  * 通过bubbleRef.current.style.opacity = '0'或'1' 让气泡消失有过渡效果
 *
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Bubble } from 'tea-component';
import type { BubbleProps } from 'tea-component';

// HeaderCellBubble 内部对 Bubble 的 visible/content/referenceElement/className 等进行受控逻辑，
// 外部仅可透传其余展示/定位/样式相关配置。
type THeaderCellBubbleConfig = Omit<
  BubbleProps,
  'content' | 'visible' | 'referenceElement' | 'className' | 'onClick' | 'onMouseEnter' | 'onMouseLeave'
>;

// 定义组件props类型
interface IHeaderCellBubbleProps extends THeaderCellBubbleConfig {
  headerRef: React.RefObject<HTMLElement>; // 表头元素的ref
  headerCellBubbleMap?: Record<string, (info: IBubbleInfo) => React.ReactNode>; // 气泡内容映射表
}

// 定义气泡信息类型
interface IBubbleInfo {
  type: string; // 气泡类型：'title' | 'sort' | ''
  element?: HTMLElement; // 关联的DOM元素
  descriptionKey?: string; // 描述文本的key（当type为'description'时）
  sortNumber?: string; // 排序序号（当type为'sort'时）
  order?: string; // 排序顺序（当type为'sort'时）
  sortCount?: number; // 有几列排序
}

const HeaderCellBubble: React.FC<IHeaderCellBubbleProps> = (props) => {
  const { headerRef, headerCellBubbleMap, ...restProps } = props;
  // 气泡元素
  const bubbleRef = React.useRef<HTMLDivElement | null>(null);
  // 定时器
  const timeoutRef = React.useRef<number | null>(null);
  // 使用状态管理气泡信息
  const [headerBubbleInfo, setHeaderBubbleInfo] = useState<IBubbleInfo>({
    type: '',
    element: undefined,
  });

  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = null;
  }, []);

  const closeBubbleWithDelay = useCallback((delay: number) => {
    bubbleRef.current?.style && (bubbleRef.current.style.opacity = '0');
    clearPendingTimeout();
    timeoutRef.current = window.setTimeout(() => {
      setHeaderBubbleInfo({ type: '', element: undefined });
    }, delay);
  }, [clearPendingTimeout]);

  useEffect(() => {
    const headerEl = headerRef?.current;
    if (!headerEl) {
      return undefined;
    }

    // 分组表头会生成多行 `.art-table-header-row`。
    // 这里直接在表头容器上做事件代理，保证叶子列所在的最底层表头也能命中。
    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const element =
        target.closest('.dbl-multiple-sort-header-cell-sort') ||
        target.closest('.dbl-multiple-sort-header-cell-text');
      if (!element) {
        return;
      }

      if (!(element instanceof HTMLElement)) {
        return;
      }

      const { descriptionKey, sortNumber, order, sortCount } = element.dataset;
      const info: IBubbleInfo = { type: '' };

      if (descriptionKey) {
        info.type = 'title';
        info.descriptionKey = descriptionKey;
      } else {
        info.type = order === 'none' ? '' : 'sort';
        info.sortNumber = sortNumber;
        info.order = order;
        info.sortCount = Number(sortCount) || 0;
      }

      clearPendingTimeout();
      bubbleRef.current?.style &&
        (bubbleRef.current.style.opacity = info.type ? '1' : '0');
      setHeaderBubbleInfo(() => ({
        ...info,
        element: info.type ? (element as HTMLElement) : undefined,
      }));
    };

    const handleMouseOut = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const relatedTarget = event.relatedTarget as HTMLElement | null;

      if (!target) {
        return;
      }

      if (
        !relatedTarget?.closest('.dbl-multiple-sort-header-cell-text') &&
        target.closest('.dbl-multiple-sort-header-cell-text')
      ) {
        closeBubbleWithDelay(3000);
      }

      if (
        !relatedTarget?.closest('.dbl-multiple-sort-header-cell-sort') &&
        target.closest('.dbl-multiple-sort-header-cell-sort')
      ) {
        closeBubbleWithDelay(300);
      }
    };

    headerEl.addEventListener('mouseover', handleMouseOver as EventListener);
    headerEl.addEventListener('mouseout', handleMouseOut as EventListener);

    return () => {
      clearPendingTimeout();
      headerEl.removeEventListener('mouseover', handleMouseOver as EventListener);
      headerEl.removeEventListener('mouseout', handleMouseOut as EventListener);
    };
  }, [clearPendingTimeout, closeBubbleWithDelay, headerRef]);
  // 渲染气泡组件
  return (
    <Bubble
      ref={bubbleRef}
      content={headerCellBubbleMap?.[headerBubbleInfo.type]?.(headerBubbleInfo)}
      visible={!!headerBubbleInfo.element}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
      }}
      className="dbl-ali-analysis-table-header-bubble"
      referenceElement={headerBubbleInfo.element}
      onMouseEnter={() => {
        // 鼠标滑入时 取消 之前定时关闭气泡的setTimeout
        if (timeoutRef.current != null) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = null;
        bubbleRef.current?.style && (bubbleRef.current.style.opacity = '1');
      }}
      onMouseLeave={() => {
        // 鼠标离开气泡后短延迟关闭，允许自然过渡动画结束
        closeBubbleWithDelay(300);
      }}
      {...restProps}
    />
  );
};

export default HeaderCellBubble;